/**
 * Authentication Service
 *
 * Handles all authentication-related API calls:
 * - Login/logout
 * - Token refresh
 * - Registration
 * - Password reset
 * - Email verification
 *
 * @see WICL-DOC-002-A Authentication System
 */

import { api } from '@/lib/api';

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  acceptTerms: boolean;
  invitationCode?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  emailVerified: boolean;
  /**
   * Avatar reference, exactly as login / refresh / `/auth/me` return it. This used to be typed
   * as `avatarUrl`, a name no endpoint ever sends, so the header avatar was empty on every load
   * and only appeared after a profile update wrote the alias client-side.
   */
  profilePictureUrl?: string;
  language?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
}

export interface VerifyEmailRequest {
  token: string;
}

/**
 * Linked OAuth Account
 */
export interface LinkedAccount {
  id: string;
  provider: 'google' | 'github' | 'microsoft';
  providerAccountId: string;
  email: string;
  isPrimary: boolean;
  linkedAt: string;
}

/**
 * Authentication Service Methods
 */
export const authService = {
  /**
   * Login user
   */
  login: (data: LoginRequest): Promise<LoginResponse> => {
    return api.post('/auth/login', data);
  },

  /**
   * Register new user
   */
  register: (data: RegisterRequest): Promise<LoginResponse> => {
    return api.post('/auth/register', data);
  },

  /**
   * Logout user
   */
  logout: (refreshToken: string | null): Promise<void> => {
    // The refresh token is what the server revokes; without it the session stayed usable
    // for its full lifetime after the sign-out.
    return api.post('/auth/logout', { refreshToken }, {
      _skipAuthRetry: true, // Skip token refresh retry for logout
    });
  },

  /**
   * Refresh access token
   */
  refreshToken: (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    return api.post('/auth/refresh', data);
  },

  /**
   * Request password reset email
   */
  forgotPassword: (data: ForgotPasswordRequest): Promise<void> => {
    return api.post('/auth/forgot-password', data);
  },

  /**
   * Reset password with token
   */
  resetPassword: (data: ResetPasswordRequest): Promise<void> => {
    return api.post('/auth/reset-password', data);
  },

  /**
   * Verify email with token
   */
  verifyEmail: (data: VerifyEmailRequest): Promise<void> => {
    return api.post('/auth/verify-email', data);
  },

  /**
   * Resend verification email
   */
  resendVerificationEmail: (): Promise<void> => {
    return api.post('/auth/resend-verification');
  },

  /**
   * Get current user session
   */
  getSession: (): Promise<User> => {
    return api.get('/auth/me');
  },

  /**
   * Get linked OAuth accounts
   */
  getLinkedAccounts: (): Promise<LinkedAccount[]> => {
    return api.get('/auth/linked-accounts');
  },

  /**
   * Unlink OAuth account
   */
  unlinkAccount: (provider: string): Promise<void> => {
    return api.delete(`/auth/unlink/${provider}`);
  },

  /**
   * Update user language preference
   */
  updateLanguagePreference: (language: string): Promise<void> => {
    return api.patch('/auth/me/language', { language });
  },
};
